import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import PermissionRoute from '../../components/routing/PermissionRoute';
import { useAuth } from '../../hooks/useAuth';
import PromoCodesPage from './PromoCodesPage';
import GiftCardsPage from './GiftCardsPage';
import styles from './OffersCreditsPage.module.css';

/**
 * OffersCreditsPage - Hub that composes the existing promo-code and gift pages
 * into one tabbed workspace. Each tab renders its ORIGINAL component by import;
 * neither page is rewritten or merged, so both stay independently routable.
 *
 * `Gift Credit` and `Gift Memberships` are TWO DIFFERENT SYSTEMS that happen to
 * share one source page: a `gift_cards` code redeems to account credit, while a
 * `gifted_memberships` short code is claimed at /gift/<code>. GiftCardsPage
 * renders both stacked; its `section` prop picks one half so this hub can give
 * each its own tab without duplicating ~250 lines of that page's logic here.
 *
 * Each tab keeps the exact permission its standalone route carries in App.jsx,
 * through the SAME <PermissionRoute> gate rather than a second hand-rolled copy
 * of the check.
 */

const TABS = [
  {
    key: 'promo-codes',
    label: 'Promo Codes',
    permission: 'promo_codes.view',
    Component: PromoCodesPage
  },
  {
    key: 'gift-credit',
    label: 'Gift Credit',
    permission: 'gift_cards.view',
    Component: GiftCardsPage,
    props: { section: 'credit' }
  },
  {
    key: 'gift-memberships',
    label: 'Gift Memberships',
    permission: 'gift_cards.view',
    Component: GiftCardsPage,
    props: { section: 'memberships' }
  }
];

const findTab = (key) => TABS.find((tab) => tab.key === key);

/**
 * A `?tab=` value selects a tab whenever it NAMES one — including a tab the role
 * cannot see, which then renders the access-denied panel instead of the content.
 * Silently redrawing a forbidden deep link as a permitted tab would hide the
 * refusal. Only an unrecognised value falls back, and it falls back to the first
 * tab this role can actually see so the page is never blank.
 */
const resolveActiveKey = (requested, visibleTabs) =>
  findTab(requested)?.key ?? visibleTabs[0]?.key ?? TABS[0].key;

const OffersCreditsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { hasPermission, isLoading } = useAuth();

  const visibleTabs = useMemo(
    () => TABS.filter(({ permission }) => hasPermission(permission)),
    [hasPermission]
  );

  const activeKey = resolveActiveKey(searchParams.get('tab'), visibleTabs);
  const activeTab = findTab(activeKey) ?? TABS[0];
  const { Component } = activeTab;

  // The tab lives in the same searchParams as any child filter, so it survives a reload.
  const handleTabChange = useCallback(
    (key) => {
      const params = new URLSearchParams(searchParams);
      params.set('tab', key);
      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  return (
    <div className={styles.page}>
      <span className={styles.eyebrow}>Offers &amp; Credits</span>

      {/* Hidden while auth resolves: an empty strip would otherwise flash, then
          repaint with tabs, reading as a permission change that never happened. */}
      {!isLoading && (
        <div className={styles.tabs} role="tablist" aria-label="Offers and credits">
          {visibleTabs.map(({ key, label }) => (
            <button
              key={key}
              id={`offers-tab-${key}`}
              type="button"
              role="tab"
              aria-selected={activeKey === key}
              aria-controls={`offers-panel-${key}`}
              className={`${styles.tab} ${activeKey === key ? styles.tabActive : ''}`}
              onClick={() => handleTabChange(key)}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      <div
        id={`offers-panel-${activeKey}`}
        role="tabpanel"
        aria-labelledby={`offers-tab-${activeKey}`}
      >
        <PermissionRoute permission={activeTab.permission}>
          <Component {...(activeTab.props ?? {})} />
        </PermissionRoute>
      </div>
    </div>
  );
};

export default OffersCreditsPage;
